<template>
    <input
        type="text"
        :value="currentValue"
        :placeholder="$t('datepicker.placeholder')"
        class="kgr-input"
        :class="{ 'is-invalid': invalid }"
        :disabled="disabled"
        @input="input($event.target.value)"
        @blur="blur($event.target.value)"
        @change="change($event.target.value)"
        @keyup.backspace="closeDatepicker"
        @keyup.enter="handleEnter"
    />
</template>

<script>
import moment from 'moment';

const INVALID_VALUE = null;

export default {
    compatConfig: {
        MODE: 3,
    },

    props: {
        modelValue: {
            type: String,
            required: false,
            example: '2018-10-30',
        },
        value: {
            type: String,
            required: false,
        },
        showOnInit: {
            type: Boolean,
            required: false,
            default: false,
        },
        invalid: {
            type: Boolean,
            required: false,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        minDate: {
            type: Date,
            required: false,
            default: null,
        },
    },

    emits: ['update:modelValue', 'input', 'post-input', 'inputIso8601', 'blur', 'change', 'enter'],

    data() {
        return {
            currentValue: this.modelValue ?? this.value ?? '',
            dateInvalid: false,
        };
    },

    watch: {
        modelValue(newValue) {
            if (newValue === INVALID_VALUE && this.dateInvalid) {
                return;
            }
            if (newValue === this.currentValue) {
                return;
            }

            this.currentValue = newValue;
            this.dateInvalid = false;
        },
        value(newValue) {
            if (newValue === INVALID_VALUE && this.dateInvalid) {
                return;
            }
            if (newValue === this.currentValue) {
                return;
            }

            this.currentValue = newValue;
            this.dateInvalid = false;
        },
        minDate(newValue, oldValue) {
            // Compared by value, not by identity: jQuery UI's option setter hides an open calendar
            // before it applies anything, so re-announcing the same date closes the picker under the
            // user's cursor. A parent that builds the prop inline hands us a new Date on every render,
            // and one of those renders lands between opening the calendar and clicking a day - the
            // click then reaches nothing and the field stays empty (KIGAROO-11327).
            if (newValue?.getTime() === oldValue?.getTime()) {
                return;
            }

            $(this.$el).datepicker('option', 'minDate', newValue);
        },
    },

    mounted() {
        $(this.$el).datepicker({
            minDate: this.minDate,
            onSelect: (date) => {
                this.$el.value = date;
                this.change(date);
                this.input(date);
            },
        });

        if (this.showOnInit) {
            $(this.$el).datepicker('show');
        }
    },

    methods: {
        input(value) {
            this.handleUpdate('input', value);
        },
        blur(value) {
            this.handleUpdate('blur', value);
        },
        change(value) {
            this.handleUpdate('change', value);
        },
        handleUpdate(event, value) {
            if (!value) {
                value = null;
            }

            this.currentValue = value;

            // validate date strings
            const momentDateFormat = this.mapDateFormat();
            const date = this.isDateStringValid(this.currentValue);
            if (date) {
                this.currentValue = date.format(momentDateFormat);
                this.dateInvalid = false;
            } else {
                this.dateInvalid = true;
            }

            if (this.dateInvalid) {
                this.$emit('update:modelValue', INVALID_VALUE);
                this.$emit(event, INVALID_VALUE);
                if (event === 'input') {
                    this.$emit('post-input', INVALID_VALUE);
                }
                this.$emit('inputIso8601', INVALID_VALUE);
                return;
            }

            this.$emit('update:modelValue', this.currentValue);
            // support legacy dependency on 'input' event
            this.$emit(event, this.currentValue);
            if (event === 'input') {
                this.$emit('post-input', this.currentValue);
            }

            if (momentDateFormat) {
                this.$emit('inputIso8601', value ? moment(value, momentDateFormat).format('YYYY-MM-DD') : this.invalidValue);
            }
        },

        handleEnter() {
            const momentDateFormat = this.mapDateFormat();
            const date = this.isDateStringValid(this.currentValue);
            this.dateInvalid = !date;
            if (!date) {
                this.currentValue = INVALID_VALUE;
            }

            this.handleUpdate('change', date ? date.format(momentDateFormat) : INVALID_VALUE);

            $(this.$el).datepicker('hide');

            this.$emit('enter');
            this.$el.blur();
        },

        closeDatepicker() {
            $(this.$el).datepicker('hide');
        },

        isDateStringValid(str) {
            const momentDateFormat = this.mapDateFormat();
            const date = moment(str, momentDateFormat, true);
            return date.isValid() ? date : null;
        },

        mapDateFormat() {
            switch ($(this.$el).datepicker('option', 'dateFormat')) {
                case 'dd.mm.yy':
                    return 'DD.MM.YYYY';
                default:
                    console.error('No mapping for date format.');
                    return null;
            }
        },
    },
};
</script>
